import { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from "react";
import T from "./theme";
import { GS } from "./components/ui/GlobalStyles";
import { supabase } from "./lib/supabase";
import { DEMO_CONDO_NAME, SETTINGS_ENABLED } from "./constants";
import { DEMO_TICKETS, DEMO_SUPPLIERS, DEMO_DOCS } from "./demoData";
import {
  fetchTickets, createTicket, updateTicketStatus,
  fetchDocuments, createDocument, updateDocument, deleteDocument, reindexDocuments,
  fetchSuppliers, createSupplier, updateSupplier, deleteSupplier,
  fetchProfile, fetchAllProfiles,
  updateOwnProfile, adminCreateUser, adminUpdateProfile, adminResetPassword,
  fetchUserCondominiums, fetchAllCondosData, createCondominium, updatePedidoStatus,
  deletePreRegisto, deletePedidoInfo, toggleCondoActive, updateCondominium,
  createSuperUser, toggleSuperUserActive, updateSuperUser, changePassword,
  updatePreRegistoStatus, logAccess,
  fetchNotifications, createNotification, deleteNotification,
} from "./lib/db";

import { Sidebar } from "./components/layout/Sidebar";
import { TopBar } from "./components/layout/TopBar";
import { Drawer } from "./components/layout/Drawer";
import { Toast } from "./components/ui/Toast";
import { DemoBlockedPage } from "./components/ui/DemoOverlay";
import { CookieBanner } from "./components/ui/CookieBanner";
import { useIsMobile } from "./hooks/useIsMobile";
import { useIdleTimeout } from "./hooks/useIdleTimeout";

/* ── Eagerly loaded (landing/auth flow) ────────────────────────────────── */
import { LandingPage } from "./pages/LandingPage";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Login } from "./pages/Login";
import { ForceChangePassword } from "./pages/ForceChangePassword";
import { CondoSelect } from "./pages/CondoSelect";

/* ── Lazy loaded (post-login pages) ────────────────────────────────────── */
const Home            = lazy(() => import("./pages/Home").then(m => ({ default: m.Home })));
const Docs            = lazy(() => import("./pages/Docs").then(m => ({ default: m.Docs })));
const Suppliers       = lazy(() => import("./pages/Suppliers").then(m => ({ default: m.Suppliers })));
const Tickets         = lazy(() => import("./pages/Tickets").then(m => ({ default: m.Tickets })));
const Archive         = lazy(() => import("./pages/Archive").then(m => ({ default: m.Archive })));
const DocsArchive     = lazy(() => import("./pages/DocsArchive").then(m => ({ default: m.DocsArchive })));
const Users           = lazy(() => import("./pages/Users").then(m => ({ default: m.Users })));
const AssistantPage   = lazy(() => import("./pages/AssistantPage").then(m => ({ default: m.AssistantPage })));
const Notifications   = lazy(() => import("./pages/Notifications").then(m => ({ default: m.Notifications })));
const Budget          = lazy(() => import("./pages/Budget").then(m => ({ default: m.Budget })));
const Quotas          = lazy(() => import("./pages/Quotas").then(m => ({ default: m.Quotas })));
const Settings        = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const ProfileModal    = lazy(() => import("./pages/ProfileModal").then(m => ({ default: m.ProfileModal })));

/* ── Lazy loaded (cockpit — super admin only) ──────────────────────────── */
const CockpitSidebar    = lazy(() => import("./components/layout/CockpitSidebar").then(m => ({ default: m.CockpitSidebar })));
const CockpitDrawer     = lazy(() => import("./components/layout/CockpitDrawer").then(m => ({ default: m.CockpitDrawer })));
const CockpitDashboard  = lazy(() => import("./pages/cockpit/CockpitDashboard").then(m => ({ default: m.CockpitDashboard })));
const CockpitCondos     = lazy(() => import("./pages/cockpit/CockpitCondos").then(m => ({ default: m.CockpitCondos })));
const CockpitUsers      = lazy(() => import("./pages/cockpit/CockpitUsers").then(m => ({ default: m.CockpitUsers })));
const CockpitPreRegistos = lazy(() => import("./pages/cockpit/CockpitPreRegistos").then(m => ({ default: m.CockpitPreRegistos })));
const CockpitPedidos    = lazy(() => import("./pages/cockpit/CockpitPedidos").then(m => ({ default: m.CockpitPedidos })));
const CockpitGDPR       = lazy(() => import("./pages/cockpit/CockpitGDPR").then(m => ({ default: m.CockpitGDPR })));

/* ── Active Notification Modal ─────────────────────────────────────────── */
const DISMISSED_KEY = "vg-dismissed-notifs";
const getDismissed = () => { try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]"); } catch { return []; } };

const typeColorsModal = { obras: "#d97706", manutencao: "#2563eb", reuniao: "#7c3aed", evento: "#059669", aviso: "#dc2626" };
const typeLabelsModal = { obras: "Obras", manutencao: "Manutenção", reuniao: "Reunião", evento: "Evento", aviso: "Aviso" };

function ActiveNotificationModal({ notifications, onDismiss }) {
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const dismissed = useRef(getDismissed());
  const [localDismissed, setLocalDismissed] = useState(dismissed.current);

  const pending = useMemo(() =>
    notifications.filter(n => {
      const start = new Date(n.startDate + "T00:00:00");
      const end   = new Date(n.endDate   + "T00:00:00");
      return start <= today && end >= today && !localDismissed.includes(n.id);
    }),
  [notifications, today, localDismissed]);

  const current = pending[0];
  if (!current) return null;

  const color = typeColorsModal[current.type] || "#6b7280";
  const label = typeLabelsModal[current.type] || current.type;

  const handleDismiss = () => {
    const updated = [...localDismissed, current.id];
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(updated));
    dismissed.current = updated;
    setLocalDismissed(updated);
    if (onDismiss) onDismiss(current.id);
  };

  const fmtDatePt = (d) => {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.5)", animation: "fadeIn 0.2s ease" }}
      onClick={handleDismiss}>
      <div style={{ background: T.card, borderRadius: 16, width: "100%", maxWidth: 440, margin: "0 20px", boxShadow: "0 24px 48px rgba(0,0,0,0.3)", overflow: "hidden", animation: "fadeIn 0.25s ease" }}
        onClick={e => e.stopPropagation()}>
        {/* Color bar top */}
        <div style={{ height: 4, background: color }} />

        <div style={{ padding: "28px 28px 24px" }}>
          {/* Type badge + date */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color, background: `${color}18`, padding: "3px 10px", borderRadius: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {label}
            </span>
            <span style={{ fontSize: 12, color: T.textMuted }}>
              {fmtDatePt(current.startDate)} — {fmtDatePt(current.endDate)}
            </span>
            {pending.length > 1 && (
              <span style={{ fontSize: 11, color: T.textMuted, marginLeft: "auto" }}>
                1 de {pending.length}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: "0 0 12px", lineHeight: 1.35 }}>
            {current.title}
          </h3>

          {/* Description */}
          {current.description && (
            <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, margin: "0 0 8px" }}>
              {current.description}
            </p>
          )}

          {/* Fração */}
          {current.fracao && (
            <p style={{ fontSize: 12, color: T.textLight, margin: "12px 0 0" }}>
              Fração: <strong style={{ color: T.text }}>{current.fracao}</strong>
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${T.borderLight}`, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={handleDismiss} style={{
            background: T.primary, color: "#fff", border: "none", borderRadius: 8,
            padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}>
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser]           = useState(null);
  const [page, setPage]           = useState("home");
  const [users, setUsers]         = useState([]);
  const [docs, setDocs]           = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tickets, setTickets]     = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [toast, setToast]         = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [dark, setDark]           = useState(() => localStorage.getItem("vg-dark") !== "0");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [condos, setCondos]       = useState([]);
  const [activeCondo, setActiveCondo] = useState(null);
  const [cockpitMode, setCockpitMode]               = useState(false);
  const [cockpitPage, setCockpitPage]               = useState("ck-dashboard");
  const [inCondoFromCockpit, setInCondoFromCockpit] = useState(false);
  const [allCondosData, setAllCondosData]           = useState(null);
  const [screen, setScreen] = useState("landing");
  const [forceChangePassword, setForceChangePassword] = useState(false);
  const [pendingProfile, setPendingProfile] = useState(null);
  const isMobile = useIsMobile();
  const isDemo = useMemo(() => activeCondo?.name === DEMO_CONDO_NAME, [activeCondo]);

  const toggleDark = () => setDark(d => {
    const next = !d;
    localStorage.setItem("vg-dark", next ? "1" : "0");
    return next;
  });

  const showToast = (msg, type = "success") => setToast({ msg, type });

  /* ─── Idle timeout — auto-logout after 30 min inactivity ─────────────── */
  const handleIdleTimeout = useCallback(async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("vg-last-active");
    showToast("Sessão expirada por inatividade. Por favor, inicie sessão novamente.", "warning");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useIdleTimeout(handleIdleTimeout, !!user);

  /* ─── Shared state reset helpers ──────────────────────────────────────── */
  const resetDataState = () => {
    setTickets([]);
    setDocs([]);
    setSuppliers([]);
    setUsers([]);
    setNotifications([]);
  };

  /* ─── Load all app data for a specific condominium ─────────────────────── */
  const loadAllData = async (profile, condoId, condoName) => {
    // Demo condo → use static data, no DB queries
    if (condoName === DEMO_CONDO_NAME) {
      setTickets(DEMO_TICKETS);
      setDocs(DEMO_DOCS);
      setSuppliers(DEMO_SUPPLIERS);
      setUsers([]);
      return;
    }

    try {
      const canManageUsers = profile.role === "admin" || profile.isSuperAdmin;
      const [tks, dcs, sups, usrs, notifs] = await Promise.all([
        fetchTickets(condoId),
        fetchDocuments(condoId),
        fetchSuppliers(condoId),
        canManageUsers ? fetchAllProfiles(condoId) : Promise.resolve([]),
        fetchNotifications(condoId),
      ]);
      setTickets(tks);
      setDocs(dcs);
      setSuppliers(sups);
      setNotifications(notifs);
      if (canManageUsers) setUsers(usrs);
      else setUsers([]);
    } catch (err) {
      console.error("Error loading data:", err);
      showToast("Erro ao carregar dados. Tente recarregar a página.", "error");
    }
  };

  /* ─── Select a condominium and enrich user with per-condo role ─────────── */
  const applyCondoSelection = async (profile, condo, userCondos) => {
    // Enrich user object with per-condo role, fracao and permilagem
    const enrichedUser = { ...profile, role: condo.role, fracao: condo.fracao, permilagem: condo.permilagem };
    setUser(enrichedUser);
    setActiveCondo(condo);
    setCondos(userCondos);
    localStorage.setItem("vg-active-condo", condo.id);
    setPage("home");
    await loadAllData(enrichedUser, condo.id, condo.name);
    setLoading(false);
  };

  /* ─── Boot: resolve profile → condos → auto-select or show picker ──────── */
  const bootUser = async (profile) => {
    setLoading(true);

    // Log access for non-super-admin users only (fire & forget)
    if (!profile.isSuperAdmin) logAccess(profile.id).catch(() => {});

    // Super admin → cockpit mode (no condos needed)
    if (profile.isSuperAdmin) {
      try {
        const allData = await fetchAllCondosData();
        setAllCondosData(allData);
      } catch (err) {
        console.error("Error loading cockpit data:", err);
        setAllCondosData({ condos: [], superUsers: [], preRegistos: [], pedidosInfo: [], accessStats: { today: 0, month: 0, year: 0, online: 0 } });
      }
      setCockpitMode(true);
      setCockpitPage("ck-dashboard");
      setPage("cockpit");
      setUser(profile);
      setLoading(false);
      return;
    }

    setUser(profile);

    // Normal user → load their condos
    const userCondos = await fetchUserCondominiums(profile.id);
    setCondos(userCondos);

    if (userCondos.length === 0) {
      // No active condos — block access and sign out
      setLoading(false);
      showToast("A sua conta não está associada a nenhum condomínio ativo. Contacte o administrador.", "error");
      supabase.auth.signOut();
      return;
    }

    if (userCondos.length === 1) {
      // Auto-select the single condo
      applyCondoSelection(profile, userCondos[0], userCondos);
      return;
    }

    // Multiple condos — check localStorage for previously selected
    const savedId = localStorage.getItem("vg-active-condo");
    const savedCondo = savedId ? userCondos.find(c => c.id === savedId) : null;
    if (savedCondo) {
      applyCondoSelection(profile, savedCondo, userCondos);
    } else {
      // Show condo selection page
      setPage("condoSelect");
    }
  };

  /* ─── Supabase Auth listener ──────────────────────────────────────────── */
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        fetchProfile(session.user.id)
          .then(async (profile) => {
            if (!profile.active) {
              await supabase.auth.signOut();
              setScreen("login");
              showToast("O acesso a esta conta está desativado. Contacte o administrador.", "error");
              return;
            }
            if (profile.mustChangePassword) {
              setPendingProfile(profile);
              setForceChangePassword(true);
              setScreen("login");
              return;
            }
            await bootUser(profile);
          })
          .catch(console.error)
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" || !session) {
        // Only redirect to landing if user was actually logged in
        // (prevents redirect when signOut is called from the login page for disabled accounts)
        setUser(prev => {
          if (prev) {
            resetDataState();
            setCondos([]);
            setActiveCondo(null);
            localStorage.removeItem("vg-active-condo");
            setPage("home");
            setScreen("landing");
          }
          return null;
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Auth handlers ───────────────────────────────────────────────────── */
  const handleLogin = async (profile) => {
    // If must_change_password flag is set, show the change password screen first
    if (profile.mustChangePassword) {
      setPendingProfile(profile);
      setForceChangePassword(true);
      setLoading(false);
      return;
    }
    await bootUser(profile);
  };

  const handlePasswordChanged = async () => {
    setForceChangePassword(false);
    if (pendingProfile) {
      const updated = { ...pendingProfile, mustChangePassword: false };
      setPendingProfile(null);
      await bootUser(updated);
    }
  };

  const handleLogout = async () => {
    setCockpitMode(false);
    setCockpitPage("ck-dashboard");
    setInCondoFromCockpit(false);
    setAllCondosData(null);
    localStorage.removeItem("vg-last-active");
    await supabase.auth.signOut();
  };

  /* ─── Condominium switching ─────────────────────────────────────────────── */
  const handleSelectCondo = (condo) => {
    resetDataState();
    // Enrich user with per-condo role, fracao and permilagem
    const enrichedUser = { ...user, role: condo.role, fracao: condo.fracao, permilagem: condo.permilagem };
    setUser(enrichedUser);
    setActiveCondo(condo);
    localStorage.setItem("vg-active-condo", condo.id);
    setPage("home");

    loadAllData(enrichedUser, condo.id, condo.name);
    setDrawerOpen(false);
  };

  const handleChangeCondo = () => {
    setPage("condoSelect");
    setDrawerOpen(false);
  };

  /* ─── Cockpit: enter a specific condo ────────────────────────────────────── */
  const handleEnterCondoFromCockpit = (condo) => {
    setInCondoFromCockpit(true);
    // Super admin always enters as admin
    const enrichedUser = { ...user, role: "admin", fracao: null };
    setUser(enrichedUser);
    setActiveCondo(condo);
    setCondos([condo]); // Single condo context

    loadAllData(enrichedUser, condo.id, condo.name);
    setPage("home");
    setDrawerOpen(false);
  };

  /* ─── Cockpit: return from condo ─────────────────────────────────────────── */
  const handleReturnToCockpit = () => {
    setInCondoFromCockpit(false);
    setActiveCondo(null);
    setCondos([]);
    resetDataState();
    setUser(prev => ({ ...prev, role: undefined, fracao: undefined }));
    // Refresh cockpit data
    fetchAllCondosData().then(setAllCondosData).catch(console.error);
    setPage("cockpit");
    setDrawerOpen(false);
  };

  /* ─── Cockpit: super user CRUD ───────────────────────────────────────────── */
  const handleCreateSuperUser = async (data) => {
    try {
      const newUser = await createSuperUser(data);
      setAllCondosData(prev => ({
        ...prev,
        superUsers: [...prev.superUsers, newUser],
      }));
      showToast("Super administrador criado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao criar super administrador.", "error");
      throw err;
    }
  };

  const handleCreateCondo = async (data) => {
    try {
      const newCondo = await createCondominium(data);
      setAllCondosData(prev => ({
        ...prev,
        condos: [...prev.condos, {
          ...newCondo,
          ticketsAbertos: 0, ticketsAtivos: 0,
          totalUsers: 0, totalAdmins: 0,
          totalDocs: 0, totalTickets: 0,
          activity: 0, activityByPeriod: { week: 0, month: 0, year: 0 },
        }],
      }));
      showToast("Condomínio criado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao criar condomínio.", "error");
      throw err;
    }
  };

  const handleToggleSuperUser = async (u) => {
    const newActive = !u.active;
    try {
      await toggleSuperUserActive(u.id, newActive);
      setAllCondosData(prev => ({
        ...prev,
        superUsers: prev.superUsers.map(su =>
          su.id === u.id ? { ...su, active: newActive } : su
        ),
      }));
      showToast(newActive ? "Utilizador ativado" : "Utilizador desativado", "success");
    } catch (err) {
      showToast(err.message || "Erro ao alterar estado do utilizador.", "error");
    }
  };

  const handleUpdateSuperUser = async (id, patch, photoFile, photoRemoved) => {
    try {
      const updated = await updateSuperUser(id, patch, photoFile, photoRemoved);
      setAllCondosData(prev => ({
        ...prev,
        superUsers: prev.superUsers.map(su =>
          su.id === id ? updated : su
        ),
      }));
      if (id === user?.id) setUser(prev => ({ ...prev, name: updated.name, phone: updated.phone, photo: updated.photo }));
      showToast("Utilizador atualizado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar utilizador.", "error");
      throw err;
    }
  };

  const handleResetSuperPassword = async (email) => {
    try {
      await adminResetPassword(email);
      showToast(`Email de reset enviado para ${email}`, "success");
    } catch (err) {
      showToast(err.message || "Erro ao enviar email de reset.", "error");
    }
  };

  const handleUpdatePreRegisto = async (id, newStatus) => {
    try {
      await updatePreRegistoStatus(id, newStatus);
      setAllCondosData(prev => ({
        ...prev,
        preRegistos: prev.preRegistos.map(r => r.id === id ? { ...r, status: newStatus } : r),
      }));
      const labels = { convidado: "Convite enviado com sucesso", convertido: "Marcado como convertido" };
      showToast(labels[newStatus] || "Atualizado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar pré-registo.", "error");
    }
  };

  const handleUpdatePedido = async (id, newStatus) => {
    try {
      await updatePedidoStatus(id, newStatus);
      setAllCondosData(prev => ({
        ...prev,
        pedidosInfo: prev.pedidosInfo.map(p => p.id === id ? { ...p, status: newStatus } : p),
      }));
      showToast("Marcado como respondido", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar pedido.", "error");
    }
  };

  const handleDeletePreRegisto = async (id) => {
    try {
      await deletePreRegisto(id);
      setAllCondosData(prev => ({ ...prev, preRegistos: prev.preRegistos.filter(r => r.id !== id) }));
      showToast("Pré-registo eliminado", "success");
    } catch (err) { showToast(err.message || "Erro ao eliminar pré-registo.", "error"); }
  };

  const handleDeletePedido = async (id) => {
    try {
      await deletePedidoInfo(id);
      setAllCondosData(prev => ({ ...prev, pedidosInfo: prev.pedidosInfo.filter(p => p.id !== id) }));
      showToast("Pedido eliminado", "success");
    } catch (err) { showToast(err.message || "Erro ao eliminar pedido.", "error"); }
  };

  const handleToggleCondoActive = async (id, active) => {
    try {
      await toggleCondoActive(id, active);
      setAllCondosData(prev => ({
        ...prev,
        condos: prev.condos.map(c => c.id === id ? { ...c, active } : c),
      }));
      showToast(active ? "Condomínio reativado" : "Condomínio desativado", "success");
    } catch (err) { showToast(err.message || "Erro ao alterar estado do condomínio.", "error"); }
  };

  const handleEditCondo = async (id, { name, morada }) => {
    try {
      const updated = await updateCondominium(id, { name, morada });
      setAllCondosData(prev => ({
        ...prev,
        condos: prev.condos.map(c => c.id === id ? { ...c, name: updated.name, morada: updated.morada } : c),
      }));
      showToast("Condomínio atualizado com sucesso.", "success");
    } catch (err) { showToast(err.message || "Erro ao atualizar condomínio.", "error"); }
  };

  /* ─── Cockpit navigation helper ──────────────────────────────────────────── */
  const handleCockpitNavigate = (p) => { setCockpitPage(p); setDrawerOpen(false); };

  /* ─── Profile ─────────────────────────────────────────────────────────── */
  const saveProfile = async (uid, patch, photoFile, photoRemoved) => {
    try {
      const updated = await updateOwnProfile(uid, patch, photoFile, photoRemoved);
      // Preserve per-condo role/fracao/permilagem when updating profile-level fields
      setUser(prev => ({ ...updated, role: prev.role, fracao: prev.fracao, permilagem: prev.permilagem }));
      setUsers(uu => uu.map(u => u.id === updated.id ? { ...u, ...updated, role: u.role, fracao: u.fracao, permilagem: u.permilagem } : u));
      setShowProfile(false);
      showToast("Perfil atualizado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar perfil.", "error");
    }
  };

  /* ─── Ticket operations ─────────────────────────────────────────────────── */
  const handleCreateTicket = async (ticketData) => {
    try {
      const newTicket = await createTicket({ ...ticketData, condominiumId: activeCondo?.id });
      setTickets(t => [newTicket, ...t]);
      showToast("Ocorrência criada!", "success");
      return newTicket;
    } catch (err) {
      showToast(err.message || "Erro ao criar ocorrência.", "error");
      throw err;
    }
  };

  const handleUpdateTicketStatus = async (id, status) => {
    const ticket = tickets.find(t => t.id === id);
    try {
      const updated = await updateTicketStatus(id, status, ticket?.history);
      setTickets(t => t.map(x => x.id === id ? updated : x));
      showToast("Estado atualizado", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar estado.", "error");
    }
  };

  /* ─── Document operations ──────────────────────────────────────────────── */
  const handleReindexDocs = async (onProgress) => {
    try {
      const r = await reindexDocuments(activeCondo?.id, onProgress);
      if (activeCondo?.id) setDocs(await fetchDocuments(activeCondo.id));
      showToast(
        r.processed === 0
          ? "Todos os documentos já estavam indexados."
          : `Reindexação concluída: ${r.updated} atualizado(s)${r.failed ? `, ${r.failed} sem texto` : ""}.`,
        r.failed ? "warning" : "success"
      );
      return r;
    } catch (e) {
      showToast(e.message || "Erro ao reindexar documentos.", "error");
      return null;
    }
  };

  const handleCreateDoc = async (fileObj, meta) => {
    try {
      const newDoc = await createDocument(fileObj, { ...meta, condominiumId: activeCondo?.id });
      setDocs(d => [newDoc, ...d]);
      showToast("Documento adicionado!", "success");
      return newDoc;
    } catch (err) {
      showToast(err.message || "Erro ao adicionar documento.", "error");
      throw err;
    }
  };

  const handleUpdateDoc = async (id, patch) => {
    try {
      const updated = await updateDocument(id, patch);
      setDocs(d => d.map(x => x.id === id ? updated : x));
      showToast("Documento atualizado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar documento.", "error");
      throw err;
    }
  };

  const handleDeleteDoc = async (id) => {
    try {
      await deleteDocument(id);
      setDocs(d => d.filter(x => x.id !== id));
      showToast("Removido", "success");
    } catch (err) {
      showToast(err.message || "Erro ao remover documento.", "error");
    }
  };

  /* ─── Supplier operations ──────────────────────────────────────────────── */
  const handleCreateSupplier = async (form) => {
    try {
      const newSup = await createSupplier({ ...form, condominiumId: activeCondo?.id });
      setSuppliers(s => [...s, newSup]);
      showToast("Fornecedor adicionado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao criar fornecedor.", "error");
    }
  };

  const handleUpdateSupplier = async (id, form) => {
    try {
      const updated = await updateSupplier(id, form);
      setSuppliers(s => s.map(x => x.id === id ? updated : x));
      showToast("Fornecedor atualizado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar fornecedor.", "error");
    }
  };

  const handleDeleteSupplier = async (id) => {
    try {
      await deleteSupplier(id);
      setSuppliers(s => s.filter(x => x.id !== id));
      showToast("Removido", "success");
    } catch (err) {
      showToast(err.message || "Erro ao remover fornecedor.", "error");
    }
  };

  /* ─── User operations (admin only) ────────────────────────────────────── */
  const handleCreateUser = async (data) => {
    try {
      const result = await adminCreateUser({ ...data, condominiumId: activeCondo?.id });
      const { id } = result;
      // Reload all profiles for this condo to get the new user with per-condo role
      if (activeCondo?.id) {
        const usrs = await fetchAllProfiles(activeCondo.id);
        setUsers(usrs);
      } else {
        const newProfile = await fetchProfile(id);
        setUsers(uu => [...uu, newProfile]);
      }
      showToast(
        result.emailSent
          ? "Utilizador criado! As credenciais de acesso foram enviadas por email."
          : "Utilizador criado! Não foi possível enviar o email — contacte o utilizador.",
        result.emailSent ? "success" : "warning"
      );
    } catch (err) {
      showToast(err.message || "Erro ao criar utilizador.", "error");
      throw err;
    }
  };

  const handleBulkCreateUsers = async (list, { sendEmail = true } = {}) => {
    let created = 0;
    const failed = [];
    for (const data of list) {
      try {
        await adminCreateUser({ ...data, condominiumId: activeCondo?.id, sendEmail });
        created++;
      } catch (err) {
        failed.push({ email: data.email, error: err.message || "Erro desconhecido" });
      }
    }
    // Recarrega os perfis uma só vez no fim
    if (activeCondo?.id) {
      try { setUsers(await fetchAllProfiles(activeCondo.id)); } catch (_) { /* noop */ }
    }
    if (created > 0) showToast(`${created} utilizador(es) importado(s) com sucesso.`, failed.length ? "warning" : "success");
    else if (failed.length) showToast("Nenhum utilizador foi importado.", "error");
    return { created, failed };
  };

  const handleUpdateUser = async (id, patch, photoFile, photoRemoved) => {
    try {
      const updated = await adminUpdateProfile(id, patch, photoFile, photoRemoved, activeCondo?.id);
      // Sync role in local users list (use patch.role if present, otherwise keep existing)
      setUsers(uu => uu.map(u => u.id === id ? { ...u, ...updated, role: patch.role ?? u.role, fracao: patch.fracao ?? u.fracao, permilagem: patch.permilagem ?? u.permilagem } : u));
      if (id === user.id) setUser(prev => ({ ...prev, ...updated, role: prev.role, fracao: prev.fracao, permilagem: prev.permilagem }));
      showToast("Utilizador atualizado!", "success");
    } catch (err) {
      showToast(err.message || "Erro ao atualizar utilizador.", "error");
      throw err;
    }
  };

  const handleResetPassword = async (email) => {
    try {
      await adminResetPassword(email);
      showToast(`Email de reset enviado para ${email}`, "success");
    } catch (err) {
      showToast(err.message || "Erro ao enviar email de reset.", "error");
    }
  };

  const handleToggleUserActive = async (u) => {
    try {
      await handleUpdateUser(u.id, { active: !u.active });
    } catch (_) { /* toast already shown */ }
  };

  /* ─── Notification operations ──────────────────────────────────────────── */
  const handleCreateNotification = async (data) => {
    try {
      const newNotif = await createNotification({ ...data, condominiumId: activeCondo?.id });
      setNotifications(n => [newNotif, ...n]);
      showToast("Notificação criada!", "success");
      return newNotif;
    } catch (err) {
      showToast(err.message || "Erro ao criar notificação.", "error");
      throw err;
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await deleteNotification(id);
      setNotifications(n => n.filter(x => x.id !== id));
      showToast("Notificação eliminada", "success");
    } catch (err) {
      showToast(err.message || "Erro ao eliminar notificação.", "error");
    }
  };

  /* ─── Loading screen ───────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div data-theme={dark ? "dark" : "light"} style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <GS />
        <p style={{ color: T.textMuted, fontSize: 14 }}>A carregar...</p>
      </div>
    );
  }

  /* ─── Privacy policy page ──────────────────────────────────────────────── */
  if (!user && screen === "privacy") {
    return (
      <div data-theme="light">
        <GS />
        <PrivacyPolicy onBack={() => setScreen("landing")} />
        <div data-theme="dark"><CookieBanner /></div>
      </div>
    );
  }

  /* ─── Landing page ─────────────────────────────────────────────────────── */
  if (!user && screen === "landing") {
    return (
      <div data-theme="light">
        <GS />
        <LandingPage onEnterApp={() => setScreen("login")} onOpenPrivacy={() => setScreen("privacy")} />
        <div data-theme="dark"><CookieBanner /></div>
      </div>
    );
  }

  /* ─── Force change password screen ─────────────────────────────────────── */
  if (forceChangePassword) {
    return (
      <div data-theme={dark ? "dark" : "light"}>
        <ForceChangePassword
          dark={dark}
          onToggleDark={toggleDark}
          onPasswordChanged={handlePasswordChanged}
          showToast={showToast}
        />
        {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    );
  }

  /* ─── Login screen ─────────────────────────────────────────────────────── */
  if (!user) {
    return (
      <div data-theme={dark ? "dark" : "light"}>
        <Login
          onLogin={handleLogin}
          dark={dark}
          onToggleDark={toggleDark}
          onBack={() => setScreen("landing")}
        />
        <CookieBanner />
      </div>
    );
  }

  /* ─── Condo selection screen (no sidebar) ──────────────────────────────── */
  if (page === "condoSelect") {
    return (
      <div data-theme={dark ? "dark" : "light"}>
        <GS />
        <CondoSelect
          user={user}
          condos={condos}
          onSelectCondo={handleSelectCondo}
          onLogout={handleLogout}
          dark={dark}
          onToggleDark={toggleDark}
        />
      </div>
    );
  }

  /* ─── Suspense fallback for lazy-loaded pages ─────────────────────────── */
  const pageFallback = <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><p style={{ color: T.textMuted, fontSize: 14 }}>A carregar...</p></div>;

  /* ─── Cockpit mode (not drilled into a condo) ─────────────────────────── */
  if (cockpitMode && !inCondoFromCockpit) {
    return (
      <div data-theme={dark ? "dark" : "light"} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: "100vh", background: T.bg, fontFamily: "'Inter',system-ui,sans-serif", overflow: "hidden" }}>
        <GS />

        {!isMobile && (
          <CockpitSidebar
            user={user}
            cockpitPage={cockpitPage}
            setCockpitPage={handleCockpitNavigate}
            onLogout={handleLogout}
            dark={dark}
            onToggleDark={toggleDark}
            allData={allCondosData}
          />
        )}

        {isMobile && (
          <TopBar
            user={user}
            dark={dark}
            onToggleDark={toggleDark}
            isAdmin={false}
            onOpenDrawer={() => setDrawerOpen(true)}
            onEditProfile={() => {}}
          />
        )}

        {isMobile && (
          <CockpitDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            user={user}
            cockpitPage={cockpitPage}
            setCockpitPage={handleCockpitNavigate}
            onLogout={handleLogout}
            dark={dark}
            onToggleDark={toggleDark}
            allData={allCondosData}
          />
        )}

        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          <Suspense fallback={pageFallback}>
            <div style={{ padding: isMobile ? "16px" : "3rem", flex: 1, maxWidth: isMobile ? "none" : "80rem", margin: "0 auto", width: "100%" }}>
              {cockpitPage === "ck-dashboard" && <CockpitDashboard user={user} allData={allCondosData} />}
              {cockpitPage === "ck-condos"    && <CockpitCondos allData={allCondosData} onEnterCondo={handleEnterCondoFromCockpit} onCreateCondo={handleCreateCondo} onToggleCondoActive={handleToggleCondoActive} onEditCondo={handleEditCondo} />}
              {cockpitPage === "ck-users"     && <CockpitUsers allData={allCondosData} onCreateSuperUser={handleCreateSuperUser} onToggleSuperUser={handleToggleSuperUser} onUpdateSuperUser={handleUpdateSuperUser} onResetSuperPassword={handleResetSuperPassword} />}
              {cockpitPage === "ck-preregistos" && <CockpitPreRegistos allData={allCondosData} onUpdatePreRegisto={handleUpdatePreRegisto} onDeletePreRegisto={handleDeletePreRegisto} />}
              {cockpitPage === "ck-pedidos" && <CockpitPedidos allData={allCondosData} onUpdatePedido={handleUpdatePedido} onDeletePedido={handleDeletePedido} />}
            {cockpitPage === "ck-gdpr" && <CockpitGDPR allData={allCondosData} />}
            </div>
          </Suspense>
        </div>

        {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    );
  }

  /* ─── Navigation helper (closes drawer on mobile) ──────────────────────── */
  const handleNavigate = (p) => {
    if (p === "settings" && !SETTINGS_ENABLED) {
      showToast("Esta funcionalidade será desenvolvida brevemente.", "warning");
      setDrawerOpen(false);
      return;
    }
    setPage(p); setDrawerOpen(false);
  };
  const isAdmin = user.role === "admin" || user.isSuperAdmin;

  /* ─── Main app ─────────────────────────────────────────────────────────── */
  return (
    <div data-theme={dark ? "dark" : "light"} style={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: "100vh", background: T.bg, fontFamily: "'Inter',system-ui,sans-serif", overflow: "hidden" }}>
      <GS />

      {/* Desktop sidebar */}
      {!isMobile && (
        <Sidebar
          user={user}
          page={page}
          setPage={handleNavigate}
          tickets={tickets}
          onLogout={handleLogout}
          onEditProfile={isDemo ? undefined : () => setShowProfile(true)}
          dark={dark}
          onToggleDark={toggleDark}
          activeCondo={activeCondo}
          onChangeCondo={handleChangeCondo}
          condoCount={condos.length}
          onReturnToCockpit={inCondoFromCockpit ? handleReturnToCockpit : undefined}
          isDemo={isDemo}
        />
      )}

      {/* Mobile top bar */}
      {isMobile && (
        <TopBar
          user={user}
          dark={dark}
          onToggleDark={toggleDark}
          isAdmin={isAdmin}
          onOpenDrawer={() => setDrawerOpen(true)}
          onEditProfile={isDemo ? undefined : () => setShowProfile(true)}
          activeCondo={activeCondo}
          condoCount={condos.length}
        />
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          user={user}
          page={page}
          setPage={handleNavigate}
          tickets={tickets}
          onLogout={handleLogout}
          onEditProfile={isDemo ? undefined : () => { setShowProfile(true); setDrawerOpen(false); }}
          isDemo={isDemo}
          dark={dark}
          onToggleDark={toggleDark}
          activeCondo={activeCondo}
          onChangeCondo={handleChangeCondo}
          condoCount={condos.length}
          onReturnToCockpit={inCondoFromCockpit ? handleReturnToCockpit : undefined}
        />
      )}

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        <Suspense fallback={pageFallback}>
          <div style={{ padding: isMobile ? "16px" : "3rem", flex: 1, maxWidth: isMobile ? "none" : "80rem", margin: "0 auto", width: "100%" }}>
            {page === "home"        && <Home user={user} tickets={tickets} docs={docs} suppliers={suppliers} users={users} notifications={notifications} setPage={handleNavigate} isDemo={isDemo} />}
            {page === "docs"        && (isDemo
              ? <DemoBlockedPage title="Documentos" icon="📄" message="A gestão de documentos não está disponível na versão de demonstração. Subscreva para carregar, organizar e partilhar documentos com os condóminos." />
              : <Docs user={user} docs={docs} onCreateDoc={handleCreateDoc} onUpdateDoc={handleUpdateDoc} onDeleteDoc={handleDeleteDoc} onReindex={handleReindexDocs} setPage={handleNavigate} />)}
            {page === "docsArchive" && (isDemo
              ? <DemoBlockedPage title="Arquivo de Documentos" icon="📄" message="O arquivo de documentos não está disponível na versão de demonstração." />
              : <DocsArchive user={user} docs={docs} onUpdateDoc={handleUpdateDoc} onDeleteDoc={handleDeleteDoc} setPage={handleNavigate} />)}
            {page === "suppliers"   && <Suppliers user={user} suppliers={suppliers} onCreateSupplier={handleCreateSupplier} onUpdateSupplier={handleUpdateSupplier} onDeleteSupplier={handleDeleteSupplier} isDemo={isDemo} />}
            {page === "notifications" && <Notifications user={user} notifications={notifications} onCreateNotification={handleCreateNotification} onDeleteNotification={handleDeleteNotification} users={users} isDemo={isDemo} />}
            {page === "tickets"     && (isDemo
              ? <DemoBlockedPage title="Ocorrências" icon="🔧" message="A gestão de ocorrências não está disponível na versão de demonstração. Subscreva para registar, acompanhar e resolver problemas no seu condomínio." demoItems={tickets} demoType="tickets" />
              : <Tickets user={user} tickets={tickets} onCreateTicket={handleCreateTicket} onUpdateTicketStatus={handleUpdateTicketStatus} setPage={handleNavigate} />)}
            {page === "assistente"  && (isDemo
              ? <DemoBlockedPage title="Assistente IA" icon="🤖" message="O assistente inteligente não está disponível na versão de demonstração. Subscreva para ter acesso a um assistente que conhece o seu condomínio." />
              : <AssistantPage user={user} tickets={tickets} suppliers={suppliers} docs={docs} />)}
            {page === "archive"     && <Archive tickets={tickets} setPage={handleNavigate} />}
            {page === "budget"      && user.role === "admin" && <Budget user={user} activeCondo={activeCondo} users={users} showToast={showToast} onNavigate={handleNavigate} />}
            {page === "quotas"      && <Quotas user={user} activeCondo={activeCondo} users={users} showToast={showToast} onNavigate={handleNavigate} />}
            {page === "settings"    && user.role === "admin" && (
              SETTINGS_ENABLED
                ? <Settings activeCondo={activeCondo} showToast={showToast} />
                : (
                  <div style={{ animation: "fadeIn 0.3s ease", maxWidth: 520, margin: "40px auto 0", textAlign: "center" }}>
                    <div style={{ fontSize: 44 }}>🛠️</div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginTop: 12 }}>Definições</h2>
                    <p style={{ fontSize: 14, color: T.textMuted, marginTop: 8, lineHeight: 1.6 }}>
                      Esta funcionalidade será desenvolvida brevemente.
                    </p>
                  </div>
                )
            )}
            {page === "users"       && user.role === "admin" && (isDemo
              ? <DemoBlockedPage title="Gestão de Utilizadores" icon="👥" message="A gestão de utilizadores não está disponível na versão de demonstração. Subscreva para adicionar e gerir os condóminos do seu edifício." />
              : <Users users={users} currentUser={user} onCreateUser={handleCreateUser} onBulkCreateUsers={handleBulkCreateUsers} onUpdateUser={handleUpdateUser} onResetPassword={handleResetPassword} onToggleActive={handleToggleUserActive} />)}
          </div>
        </Suspense>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <Suspense fallback={null}>
        {showProfile && (
          <ProfileModal
            user={user}
            onSave={saveProfile}
            onClose={() => setShowProfile(false)}
          />
        )}
      </Suspense>

      {user && notifications.length > 0 && (
        <ActiveNotificationModal notifications={notifications} />
      )}

      <CookieBanner />
    </div>
  );
}
